import Company from 'mbs-ui-shared@models/Company';
import { ApplyToEnum } from 'mbs-ui-shared@models/remote-deploy/apply-to-enum';
import { RdConfigOsTypeEnum } from 'mbs-ui-shared@models/remote-deploy/rd-config-os-type.enum';
import { RemoteDeployConfiguration } from 'mbs-ui-shared@models/remote-deploy/remote-deploy-configuration';
import { RemoteDeployPlanModel } from 'mbs-ui-shared@models/remote-deploy/remote-deploy-plan-model';
import { WebInterfaceOptions } from 'mbs-ui-shared@models/remote-deploy/web-interface-options';
import { StorageAccountLegacy } from 'mbs-ui-shared@models/StorageAccount';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { BackupFilePlanFromStepsHelper } from 'wizards/helpers/backup-file-model-helpers/backup-file-plan-from-steps-helper';
import { WizardHelpersSettings } from 'wizards/helpers/bases/base-for-steps-helper';
import { IBBPlanFromStepsHelper } from 'wizards/helpers/image-based-wizard-helpers/ibb-plan-from-steps-helper';
import { SupportMethodsForPlanFromSteps } from 'wizards/helpers/support-methods-for-plan-from-steps';
import { BackupFileFormModel } from 'wizards/models/backupFile/backup-form-model';
import { BackupPlan } from 'wizards/models/base/backup-plan-model';
import { IBBFormModel } from 'wizards/models/ibb/ibb-form-model';
import { BackupFilter } from 'wizards/models/what-backup-tree-model';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { getBasePlanFromType } from 'wizards/utils/getBasePlanFromType';
import { CompanyBackupTemplate } from '../models/company-wizard-models';
import { borrowPlanContext, scheduleContextFromTemplate, templateAdvancedOptionsFlags } from './company-wizard-plan-context.util';

export type CompanyConfigurationContext = {
  company: Company;
  mainService: RemoteManagementWizardsService;
  nameFor: (osType: RdConfigOsTypeEnum) => string;
  storageAccountIdFor: (bucketId: string) => string;
  storageAccounts: StorageAccountLegacy[];
  storageConnectionFor: (template: CompanyBackupTemplate) => StorageConnection;
};

export function templateOsType(template: CompanyBackupTemplate): RdConfigOsTypeEnum {
  if (template.isMac) return RdConfigOsTypeEnum.macOS;

  if (template.isLinux) return RdConfigOsTypeEnum.Linux;

  return RdConfigOsTypeEnum.Windows;
}

export function templatesToConfigurations(
  templates: CompanyBackupTemplate[],
  context: CompanyConfigurationContext
): RemoteDeployConfiguration[] {
  const byOsType = new Map<RdConfigOsTypeEnum, CompanyBackupTemplate[]>();

  templates.forEach((template) => {
    const osType = templateOsType(template);

    byOsType.set(osType, [...(byOsType.get(osType) ?? []), template]);
  });

  return Array.from(byOsType, ([osType, osTemplates]) => buildConfiguration(osType, osTemplates, context));
}

function buildConfiguration(
  osType: RdConfigOsTypeEnum,
  templates: CompanyBackupTemplate[],
  context: CompanyConfigurationContext
): RemoteDeployConfiguration {
  const configuration = new RemoteDeployConfiguration();

  configuration.applyTo = ApplyToEnum.ApplyToAllSelectedCompanies;
  configuration.name = context.nameFor(osType);
  configuration.osType = osType;
  configuration.plans = templates.map((template) => buildPlanWrapper(template, context));
  configuration.selectedCompanies = [context.company.id];
  configuration.webInterfaceOptions = new WebInterfaceOptions();
  configuration.enabled = !!configuration.plans.length;

  return configuration;
}

function buildPlanWrapper(template: CompanyBackupTemplate, context: CompanyConfigurationContext): RemoteDeployPlanModel {
  const { mainService } = context;
  const restore = borrowPlanContext(mainService, scheduleContextFromTemplate(template));

  try {
    const formModel = buildFormModel(template, context.storageConnectionFor(template), mainService.backupVersionUpdated);
    const settings = buildHelperSettings(template, mainService);
    const basePlan = getBasePlanFromType(template.planType);

    if (basePlan instanceof BackupPlan) basePlan.BackupFilter = new BackupFilter();

    const plan = template.isImageBased
      ? IBBPlanFromStepsHelper.getUpdatedPlan(basePlan, formModel as IBBFormModel, settings, true, template.isUnix)
      : BackupFilePlanFromStepsHelper.getUpdatedPlan(basePlan, formModel as BackupFileFormModel, settings, true, template.isUnix);

    const wrapper = {
      AvailableFeatures: {},
      AvailableMachinePlans: null,
      DiskVolumes: null,
      IsUnix: template.isUnix,
      OverdueAfterSuccess: null,
      plan,
      PlanType: template.planType,
      Storages: null
    } as RemoteDeployPlanModel;

    // The configurations list and its sidepanel resolve the destination from Bucket + SGAccountID, which plan wizards fill on save
    const destination = context.company.inheritedDestinations?.find(({ bucketId }) => bucketId === wrapper.plan.ConnectionID);

    wrapper.plan.ID = mainService.generateUUID();
    wrapper.plan.IsArchive = true;
    wrapper.plan.Bucket = destination?.bucketName ?? wrapper.plan.Bucket;
    wrapper.plan.SGAccountID = destination?.storageAccountId ?? context.storageAccountIdFor(wrapper.plan.ConnectionID);

    mainService.updatePlanTextElements(wrapper, context.storageAccounts);
    mainService.updatePlanTextRetention(wrapper);
    mainService.setValidForRDStatuses(wrapper);
    SupportMethodsForPlanFromSteps.normalizePlanForSaveConfig(wrapper, false);

    return wrapper;
  } finally {
    restore();
  }
}

function buildFormModel(
  template: CompanyBackupTemplate,
  connection: StorageConnection,
  agentVersion: string
): BackupFileFormModel | IBBFormModel {
  const formModel = template.isImageBased ? new IBBFormModel(true, template.isUnix) : new BackupFileFormModel(true, template.isUnix);

  // The plan helpers copy storage class, S3 acceleration and the like only where these flags allow it, and a plan wizard
  // recomputes them for the chosen storage; the form model's own defaults have them all off
  formModel.advancedOptionsFlags = templateAdvancedOptionsFlags(template, connection, agentVersion);
  formModel.advancedOptionsStep = template.advancedOptions;
  formModel.compressionEncryptionStep = template.compressionEncryptionStep;
  formModel.planNameStep = { ...formModel.planNameStep, Name: template.name };
  formModel.retentionPolicyStep = template.destinationPlusRetention.retentionPolicyStep;
  formModel.scheduleStep = template.scheduleStep;
  formModel.whereBackupStep = template.destinationPlusRetention.whereBackupStep;

  if (template.isImageBased) (formModel as IBBFormModel).whatBackupStep = template.whatBackupStep;
  else (formModel as BackupFileFormModel).whatBackupTreeStep = template.whatBackupTreeStep;

  return formModel;
}

function buildHelperSettings(template: CompanyBackupTemplate, mainService: RemoteManagementWizardsService): WizardHelpersSettings {
  return {
    accounts: null,
    agentVersion: mainService.backupVersionUpdated,
    allowPrePostActions: false,
    compName: '',
    currentPrefix: mainService.currentPrefix,
    initialFormData: null,
    isNBF: true,
    isNetBuild: mainService.isNetBuild,
    isOffline: false,
    isRD: true,
    isUnix: template.isUnix,
    mode: mainService.mode,
    planType: template.planType,
    timeZoneOffset: mainService.timeZoneOffset,
    validVersionForSchedule: mainService.validVersionForSchedule
  };
}
